/**
 * Doctor API functions
 */

import { apiClient } from '../api-client';

// ============= Type Definitions =============

export interface AdherenceReviewSubmission {
  patient_id: number;
  review_notes: string;
  action_taken: string;
  next_review_date?: string;
}

export interface MedicationDoseUpdate {
  dosage: string;
  reason?: string;
}

export interface ClinicalNoteSubmission {
  patient_id: number;
  note_text: string;
  note_type?: string;
}

export const doctorsApi = {
  /**
   * Get list of all doctors
   */
  getDoctors: async (): Promise<any> => {
    return apiClient.get('/doctors');
  },

  /**
   * Get doctor by ID
   */
  getDoctor: async (doctorId: number): Promise<any> => {
    return apiClient.get(`/doctors/${doctorId}`);
  },

  /**
   * Get doctor dashboard summary (current logged-in doctor)
   */
  getDoctorDashboardSummary: async (): Promise<any> => {
    return apiClient.get('/doctors/me/summary');
  },

  /**
   * Submit adherence review for low-adherence patient
   * Used by: DoctorLowAdherenceDetail component
   */
  submitAdherenceReview: async (data: AdherenceReviewSubmission): Promise<{
    status: string;
    message: string;
    review_id: number;
  }> => {
    return apiClient.post('/doctors/adherence-review', data);
  },

  /**
   * Update medication dosage
   * Used by: PatientDetailsView component (medication management)
   */
  updateMedicationDose: async (medicationId: number, data: MedicationDoseUpdate): Promise<{
    status: string;
    message: string;
    medication_id: number;
    new_dosage: string;
  }> => {
    return apiClient.put(`/medications/${medicationId}/dose`, data);
  },

  /**
   * Save clinical note for patient
   * Used by: DoctorDashboard component
   */
  saveClinicalNote: async (data: ClinicalNoteSubmission): Promise<{
    status: string;
    message: string;
    note_id: number;
  }> => {
    return apiClient.post('/doctors/clinical-notes', data);
  },

  /**
   * Get clinical notes for patient
   * Used by: DoctorDashboard component
   */
  getClinicalNotes: async (patientId: number): Promise<{
    notes: Array<{
      id: number;
      doctor_name: string;
      note_text: string;
      note_type: string;
      created_at: string;
    }>;
  }> => {
    return apiClient.get(`/doctors/clinical-notes/${patientId}`);
  },
};